import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../ui/avatar";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Calendar,
  Clock,
  DollarSign,
  MapPin,
  MessageCircle,
  X,
  Send,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Star,
} from "lucide-react";

const dummyMessages = [
  {
    id: 1,
    sender: "homeowner",
    message:
      "Hi! Thank you for accepting this job. Are you available to start around 6:30 AM tomorrow?",
    time: "10:30 AM",
  },
  {
    id: 2,
    sender: "helper",
    message:
      "Yes, I can be there at 6:30 AM. Should I bring my own shovel or do you have one?",
    time: "10:32 AM",
  },
  {
    id: 3,
    sender: "homeowner",
    message:
      "There's a shovel in the garage. The code is 1234. Please make sure to shovel the walkway too.",
    time: "10:35 AM",
  },
  {
    id: 4,
    sender: "helper",
    message:
      "Perfect! I'll be there on time. Thanks for the details.",
    time: "10:36 AM",
  },
  {
    id: 5,
    sender: "homeowner",
    message: "Great! See you tomorrow morning.",
    time: "10:37 AM",
  },
];

export function JobDetailsDialog({
  job,
  isOpen,
  onClose,
  userRole,
}) {
  const [showChat, setShowChat] = useState(false);
  const [showQuery, setShowQuery] = useState(false);
  const [showMarkCompleted, setShowMarkCompleted] =
    useState(false);
  const [showRating, setShowRating] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [queryMessage, setQueryMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [messages, setMessages] = useState(dummyMessages);

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return (
          <CheckCircle className="w-5 h-5 text-green-600" />
        );
      case "ongoing":
        return <RefreshCw className="w-5 h-5 text-blue-600" />;
      case "cancelled":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: {
        color: "bg-green-100 text-green-800",
        text: "Completed",
      },
      ongoing: {
        color: "bg-blue-100 text-blue-800",
        text: "Ongoing",
      },
      cancelled: {
        color: "bg-red-100 text-red-800",
        text: "Cancelled",
      },
      active: {
        color: "bg-blue-100 text-blue-800",
        text: "Active",
      },
      pending: {
        color: "bg-yellow-100 text-yellow-800",
        text: "Pending",
      },
    };

    const variant = variants[status] || {
      color: "bg-gray-100 text-gray-800",
      text: "Unknown",
    };

    return (
      <Badge className={variant.color}>{variant.text}</Badge>
    );
  };

  const sendMessage = () => {
    if (newMessage.trim()) {
      const newMsg = {
        id: messages.length + 1,
        sender: userRole,
        message: newMessage,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages([...messages, newMsg]);
      setNewMessage("");
    }
  };

  const sendQuery = () => {
    if (queryMessage.trim()) {
      console.log("Query sent:", queryMessage);
      setQueryMessage("");
      setShowQuery(false);
    }
  };

  const handleMarkCompleted = () => {
    setShowMarkCompleted(false);
    setShowRating(true);
  };

  const submitRating = () => {
    console.log("Rating submitted:", rating);
    setShowRating(false);
    onClose();
  };

  if (!job) return null;

  // Main Job Details Dialog
  if (
    !showChat &&
    !showQuery &&
    !showMarkCompleted &&
    !showRating
  ) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Service Details
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Job Information */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={job.avatar} />
                <AvatarFallback className="bg-black text-white text-xl">
                  {job.homeowner?.[0] || job.helper?.[0] || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold">
                  {job.homeowner || job.helper || "User"}
                </h3>
                <p className="text-lg text-gray-600">
                  {job.service}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(job.status)}
                  {getStatusBadge(job.status)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">
                  {job.amount}
                </div>
              </div>
            </div>

            {/* Service Details */}
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">
                Service In Progress
              </h4>
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-600">
                    Address
                  </span>
                  <p className="text-lg">
                    {job.address || job.location}
                  </p>
                </div>
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-600">
                    Start Time
                  </span>
                  <p className="text-lg">{job.startTime}</p>
                </div>
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-600">
                    Duration
                  </span>
                  <p className="text-lg">{job.duration}</p>
                </div>
                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-600">
                    Date
                  </span>
                  <p className="text-lg">{job.date}</p>
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-600 underline">
                  Instructions:
                </span>
                <p className="text-base bg-white p-4 rounded-lg">
                  {job.instructions ||
                    "Shovels are inside the storm door, please leave them there after you are finished. Shovel the driveway, front stairs and walkway beside the house as well. Thanks!"}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {/* Action Buttons */}
            <div className="flex flex-col gap-4">
              {/* First row */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setShowQuery(true)}
                  className="px-6"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Query
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setShowChat(true)}
                  className="px-6"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat
                </Button>
              </div>

              {/* Second row */}
              <div className="flex gap-3">
                {userRole === "homeowner" && (
                  <Button
                    size="lg"
                    onClick={() => setShowMarkCompleted(true)}
                    className="px-6 bg-black hover:bg-gray-800 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Completed
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onClose}
                  className="px-6"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Chat Dialog
  if (showChat) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={() => setShowChat(false)}
      >
        <DialogContent className="max-w-2xl h-[600px] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              <MessageCircle className="w-6 h-6" />
              Chat with{" "}
              {userRole === "homeowner"
                ? "Helper"
                : "Homeowner"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 flex flex-col space-y-4">
            {/* Messages */}
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === userRole ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender === userRole
                          ? "bg-black text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                      <p
                        className={`text-xs mt-1 ${
                          msg.sender === userRole
                            ? "text-gray-300"
                            : "text-gray-500"
                        }`}
                      >
                        {msg.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="flex gap-2">
              <Input
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && sendMessage()
                }
                className="flex-1"
              />
              <Button
                onClick={sendMessage}
                size="sm"
                className="bg-black hover:bg-gray-800"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Query Dialog
  if (showQuery) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={() => setShowQuery(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              <HelpCircle className="w-6 h-6" />
              Submit Query
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <p className="text-gray-600">
                Have a question about this job? Type your query
                below:
              </p>
              <Textarea
                placeholder="Enter your question or concern here..."
                value={queryMessage}
                onChange={(e) =>
                  setQueryMessage(e.target.value)
                }
                className="min-h-24"
                rows={4}
              />
            </div>

            <div className="flex gap-4 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowQuery(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={sendQuery}
                className="bg-black hover:bg-gray-800 text-white"
              >
                Send Query
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Mark as Completed Dialog
  if (showMarkCompleted) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={() => setShowMarkCompleted(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">
              Service Completed
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 text-center">
            <div className="bg-gray-100 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-lg font-semibold">
                    {job.amount}
                  </p>
                  <p className="text-sm text-gray-600">
                    Will Be Transferred To Helper's Stripe
                    Account Upon Confirmation
                  </p>
                </div>
                <div className="w-10 h-10 bg-black rounded flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-gray-200 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-gray-500" />
              </div>
              <h3 className="text-xl text-gray-600">
                Thank you for using ShovelHouse!
              </h3>
            </div>

            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => setShowMarkCompleted(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkCompleted}
                className="bg-black hover:bg-gray-800 text-white"
              >
                Confirm Service Completed
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Rating Dialog
  if (showRating) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={() => setShowRating(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">
              Service Finished
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 text-center">
            <div className="bg-gray-100 p-6 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-lg font-semibold">
                    {job.amount}
                  </p>
                  <p className="text-sm text-gray-600">
                    Will Be Transferred To Your Stripe Account
                    Upon Homeowner's Confirmation of Service
                    within the next 24 hours.
                  </p>
                </div>
                <div className="w-10 h-10 bg-black rounded flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto bg-gray-200 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-gray-500" />
              </div>
              <h3 className="text-xl text-gray-600">
                Thank You For Helping A Neighbour!
              </h3>
            </div>

            <div className="bg-gray-100 p-4 rounded-xl">
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Rate{" "}
                  {userRole === "homeowner"
                    ? "Helper"
                    : "Homeowner"}
                  's Job Posting
                </p>
                <div className="flex justify-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-6 h-6 cursor-pointer ${
                        star <= rating
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                      onClick={() => setRating(star)}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Share your experience anonymously and help us
                  improve the community without revealing your
                  identity
                </p>
              </div>
            </div>

            <Button
              onClick={submitRating}
              className="w-full bg-black hover:bg-gray-800 text-white"
            >
              Continue Back to Homepage
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
}